/**
 * useSeoHead — Lightweight per-route SEO head manager
 *
 * Why not react-helmet-async? — Adds a dep, requires wrapping the app, and we
 * already have direct DOM manipulation patterns in Capability/PlantProfile.
 * This hook centralizes the pattern: <title>, meta description, canonical,
 * og:*, hreflang ES/EN/x-default, and a structured-data block keyed by id.
 *
 * All elements are tagged `data-cd-seo` so they can be cleaned up by the next
 * route's call to the hook, preventing stale tags from accumulating across
 * navigation.
 */
import { useEffect } from "react";

const BASE = "https://corridordigital.net";

function setMeta(name, content, isProperty = false) {
  if (content == null) return;
  const attr = isProperty ? "property" : "name";
  // Prefer reusing an SSR-injected tag if one exists; otherwise our own.
  let el =
    document.head.querySelector(`meta[${attr}="${name}"][data-cd-ssr]`) ||
    document.head.querySelector(`meta[${attr}="${name}"][data-cd-seo]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    el.setAttribute("data-cd-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, attrs = {}) {
  if (!href) return;
  const key = Object.entries({ rel, ...attrs })
    .map(([k, v]) => `${k}="${v}"`)
    .join("");
  const sel = `link[data-cd-key="${CSS.escape(key)}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute("data-cd-key", key);
    el.setAttribute("data-cd-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, payload) {
  // Remove first to handle updates cleanly
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!payload) return;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.setAttribute("data-cd-seo", "1");
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
}

/**
 * @param {object} cfg
 *  - title: string
 *  - description: string
 *  - path: string (pathname starting with "/") — used for canonical + hreflang
 *  - ogImage: optional override (defaults to /og-default.png)
 *  - jsonLd: optional [{ id, payload }] array
 *  - lang: "es" | "en"
 */
export default function useSeoHead({
  title,
  description,
  path,
  ogImage,
  jsonLd,
  lang,
}) {
  useEffect(() => {
    if (!title || !path) return;
    document.title = title;
    setMeta("description", description);

    // Belt-and-suspenders: remove any pre-existing canonical/hreflang link tags
    // not owned by us (e.g. baked into public/index.html) so we don't end up
    // with duplicates that confuse search engines. SSR-injected tags
    // (`data-cd-ssr`) are kept and updated in place.
    document
      .querySelectorAll("link[rel=canonical]:not([data-cd-seo]):not([data-cd-ssr])")
      .forEach((el) => el.remove());
    document
      .querySelectorAll("link[rel=alternate][hreflang]:not([data-cd-seo]):not([data-cd-ssr])")
      .forEach((el) => el.remove());

    // Update SSR-injected canonical/hreflang in place if present
    const ssrCanon = document.head.querySelector("link[rel=canonical][data-cd-ssr]");
    if (ssrCanon) {
      ssrCanon.setAttribute("href", `${BASE}${path}`);
    }
    document
      .querySelectorAll("link[rel=alternate][hreflang][data-cd-ssr]")
      .forEach((el) => {
        const hreflang = el.getAttribute("hreflang");
        const url =
          hreflang === "en"
            ? `${BASE}${path}${path.includes("?") ? "&" : "?"}lang=en`
            : `${BASE}${path}`;
        el.setAttribute("href", url);
      });

    // Canonical — only insert one if SSR didn't already render it (which it
    // does for every primary route).
    const canonicalUrl = `${BASE}${path}`;
    if (!document.head.querySelector("link[rel=canonical][data-cd-ssr]")) {
      setLink("canonical", canonicalUrl);
    }

    // hreflang — ES is default, EN uses ?lang=en (skip if SSR rendered them)
    if (!document.head.querySelector("link[rel=alternate][hreflang][data-cd-ssr]")) {
      setLink("alternate", canonicalUrl, { hreflang: "es" });
      setLink(
        "alternate",
        `${canonicalUrl}${path.includes("?") ? "&" : "?"}lang=en`,
        { hreflang: "en" },
      );
      setLink("alternate", canonicalUrl, { hreflang: "x-default" });
    }

    // Open Graph
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", "website", true);
    setMeta(
      "og:image",
      ogImage || `${BASE}/og-default.png`,
      true,
    );
    setMeta("og:image:width", "1200", true);
    setMeta("og:image:height", "630", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // HTML root lang
    if (lang) document.documentElement.setAttribute("lang", lang);

    // JSON-LD blocks
    if (Array.isArray(jsonLd)) {
      jsonLd.forEach((block) => {
        if (block && block.id && block.payload) {
          setJsonLd(block.id, block.payload);
        }
      });
    }

    return () => {
      // Clean up route-specific JSON-LD blocks on unmount so next page starts fresh
      if (Array.isArray(jsonLd)) {
        jsonLd.forEach((block) => {
          if (block && block.id) {
            const el = document.getElementById(block.id);
            if (el) el.remove();
          }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, ogImage, lang, JSON.stringify(jsonLd)]);
}

/** Helper: BreadcrumbList JSON-LD payload */
export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
